import api from './axios';
import { API_ENDPOINTS, getFormName, getSiteGroupId } from '../constants';
import { logError } from '../common/utils/logger';
import { encryptJourneyQueryValue } from '../common/utils/journeyEncryption';

export const getFormDefinition = async ({ stage = 'lead', formName, additionalParams = {} } = {}) => {
  const resolvedFormName = formName || getFormName(stage);
  const siteGroupId = getSiteGroupId();

  if (!resolvedFormName) {
    throw new Error(`Form name is not configured for stage "${stage}".`);
  }

  if (siteGroupId === null || siteGroupId === undefined) {
    throw new Error('Site group ID is not available. Ensure the app is running within Liferay context.');
  }

  const response = await api.get(API_ENDPOINTS.GET_FORM_DEFINITION, {
    params: {
      siteGroupId,
      formName: resolvedFormName,
      ...additionalParams,
    },
  });

  return response?.data || null;
};

/**
 * Fetch form definition from API by form name and site group ID
 * @param {string} stage - Journey stage (lead, quote, etc.) - defaults to 'lead'
 * @param {Object} additionalParams - Additional parameters to send with request (e.g., PolicyNo)
 * @returns {Promise<Object>} Form definition object
 */
export const fetchFormDefinition = async (stage = 'lead', additionalParams = {}) => {
  try {
    return await getFormDefinition({ stage, additionalParams });
  } catch (error) {
    logError('[fetchFormDefinition] Failed to fetch form definition', error);
    throw error;
  }
};

/**
 * Submit form data for Term Journey Lead.
 * Uses POST for create flow (no application number) and PATCH for updates.
 * @param {Object} formData - Form data to submit
 * @returns {Promise<Object>} Submission response
 */
export const submitFormData = async (formData) => {
  try {
    const method = 'post';
    const payload = { ...(formData || {}) };
    if (payload.applicationNumber != null && String(payload.applicationNumber).trim() !== '') {
      payload.applicationNumber = encryptJourneyQueryValue(payload.applicationNumber);
    }
    if (payload.dateOfBirth != null && String(payload.dateOfBirth).trim() !== '') {
      payload.dateOfBirth = encryptJourneyQueryValue(payload.dateOfBirth);
    }
    if (payload.ownershipDateOfBirth != null && String(payload.ownershipDateOfBirth).trim() !== '') {
      payload.ownershipDateOfBirth = encryptJourneyQueryValue(payload.ownershipDateOfBirth);
    }

    const response = await api[method](API_ENDPOINTS.SUBMIT_LEAD, {
      ...payload
    });

    return response?.data || null;
  } catch (error) {
    logError('[submitFormData] Failed to submit form', error);
    throw error;
  }
};
const ORGANIZATION_MASTER_TYPE = 'organizationdetailsmasters';
const ORGANIZATION_SUGGESTION_LIMIT = 25;
export const ORGANIZATION_PREFIX_MIN_LENGTH = 3;

// In-memory cache for full pincode master (bulk load — Proposal dropdowns / fallback only)
let pincodeCache = null;
let pincodeCachePromise = null;

const PINCODE_MASTER_TYPE = 'getProductPincodes';
const PINCODE_SUGGESTION_LIMIT = 25;

const normalizePincode = (value) => String(value ?? '').replace(/\D/g, '');
const getItemPincode = (item) => item?.pincode ?? item?.pinCode ?? '';

const parseJourneyMasterItems = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      if (Array.isArray(parsed)) return parsed;
      if (Array.isArray(parsed?.items)) return parsed.items;
    } catch {
      // ignore malformed JSON
    }
  }
  return [];
};

const filterPincodeItemsByPrefix = (items, query) =>
  (items || [])
    .filter((item) => {
      const pin = String(getItemPincode(item) ?? '');
      return pin && pin.startsWith(query);
    })
    .sort((a, b) => {
      const aPin = String(getItemPincode(a) ?? '');
      const bPin = String(getItemPincode(b) ?? '');
      const aNum = parseInt(aPin, 10);
      const bNum = parseInt(bPin, 10);
      return !isNaN(aNum) && !isNaN(bNum) ? aNum - bNum : aPin.localeCompare(bPin);
    });

/**
 * Server-side prefix search (3–6 digits). Does not download the full master.
 */
const fetchPincodesByPrefix = async (prefix) => {
  const response = await api.get(API_ENDPOINTS.JOURNEY_MASTERS, {
    params: {
      masterType: PINCODE_MASTER_TYPE,
      pincodePrefix: prefix,
    },
  });
  return parseJourneyMasterItems(response?.data);
};

/**
 * Pre-fetch pincode data on page load
 */
export const preloadPincodeData = async () => {
  try {
    await fetchAllPincodes();
  } catch (error) {
    logError('[preloadPincodeData] Failed to pre-load pincode data', error);
  }
};

/**
 * Fetch all pincodes from becomeAnAdvisor API (in-memory cache only)
 */
const getPincodeHeaders = () => {
  const headers = { 'Content-Type': 'application/json' };

  if (typeof Liferay !== 'undefined' && Liferay.authToken) {
    headers['x-csrf-token'] = Liferay.authToken;
  }

  return headers;
};

const getPincodeItems = (data) => {
  if (Array.isArray(data?.items)) {
    return data.items;
  }

  if (Array.isArray(data)) {
    return data;
  }

  return [];
};

/**
 * Fetch all pincodes from becomeAnAdvisor API (in-memory cache only)
 */
const fetchAllPincodes = async () => {
  if (pincodeCache) return pincodeCache;
  if (pincodeCachePromise) return pincodeCachePromise;

  const headers = getPincodeHeaders();

  pincodeCachePromise = api
    .get(API_ENDPOINTS.PINCODE, { headers })
    .then(async (response) => {
      const data = response?.data || {};

      let items = getPincodeItems(data);

      const paginationData = Array.isArray(data) ? {} : data;
      const { lastPage, pageSize } = paginationData;

      // Fetch remaining pages if pagination detected
      if (lastPage > 1 && pageSize) {
        const pages = Array.from(
          { length: lastPage - 1 },
          (_, i) => i + 2
        );

        const batchPromises = pages.map((page) =>
          api
            .get(API_ENDPOINTS.PINCODE, {
              headers,
              params: { page, pageSize },
            })
            .then((r) => r?.data?.items || [])
            .catch(() => [])
        );

        const batchResults = await Promise.all(batchPromises);
        items = [...items, ...batchResults.flat()];
      }

      pincodeCache = items;
      pincodeCachePromise = null;

      return items;
    })
    .catch((error) => {
      logError('[fetchAllPincodes] Failed to fetch pincodes', error);
      pincodeCachePromise = null;
      return [];
    });

  return pincodeCachePromise;
};

const fetchOrganizationsByPrefix = async (prefix, { exact = false } = {}) => {
  const response = await api.get(API_ENDPOINTS.JOURNEY_MASTERS, {
    params: {
      masterType: ORGANIZATION_MASTER_TYPE,
      organizationPrefix: String(prefix ?? '').trim(),
      ...(exact ? { organizationExact: true } : {}),
    },
  });
  return parseJourneyMasterItems(response?.data);
};

export const getPincodeMasterRecord = async (pincode) => {
  const normalizedPincode = normalizePincode(pincode);
  if (!normalizedPincode) return null;

  if (normalizedPincode.length >= 3) {
    try {
      const items = await fetchPincodesByPrefix(normalizedPincode);
      const exact = items.find(
        (item) => normalizePincode(getItemPincode(item)) === normalizedPincode,
      );
      if (exact) return exact;
      if (normalizedPincode.length === 6) {
        return null;
      }
    } catch (error) {
      logError('[getPincodeMasterRecord] Prefix lookup failed, falling back to bulk', error);
    }
  }

  const items = await fetchAllPincodes();
  if (!Array.isArray(items) || items.length === 0) return null;

  return (
    items.find((item) => normalizePincode(getItemPincode(item)) === normalizedPincode) ??
    null
  );
};

/**
 * Fetch pincode suggestions based on query
 * @param {string} pincodeQuery - Pincode query string (minimum 3 characters)
 * @returns {Promise<Array>} Array of matching pincode suggestions
 */
export const fetchPincodeSuggestions = async (pincodeQuery) => {
  const query = normalizePincode(pincodeQuery);
  if (query.length < 3) return [];

  try {
    const items = await fetchPincodesByPrefix(query);
    return items.slice(0, PINCODE_SUGGESTION_LIMIT);
  } catch (error) {
    logError('[fetchPincodeSuggestions] Prefix search failed, falling back to bulk', error);
    try {
      const items = await fetchAllPincodes();
      return filterPincodeItemsByPrefix(items, query).slice(0, PINCODE_SUGGESTION_LIMIT);
    } catch (fallbackError) {
      logError('[fetchPincodeSuggestions] Failed to fetch suggestions', fallbackError);
      return [];
    }
  }
};

export const fetchOrganizationSuggestions = async (organizationQuery) => {
  const prefix = String(organizationQuery ?? '').trim();
  if (!prefix || prefix.length < ORGANIZATION_PREFIX_MIN_LENGTH) return [];

  try {
    const items = await fetchOrganizationsByPrefix(prefix);
    return items.slice(0, ORGANIZATION_SUGGESTION_LIMIT);
  } catch (error) {
    logError('[fetchOrganizationSuggestions] Prefix search failed', error);
    return [];
  }
};

/**
 * Checks whether an organisation name exactly matches a listed master record.
 * @param {string} orgName - Organisation name to verify
 * @returns {Promise<boolean>}
 */

export const checkOrganisationListed = async (orgName) => {
  const needle = String(orgName ?? '').trim();
  if (
    !needle ||
    needle.length < ORGANIZATION_PREFIX_MIN_LENGTH ||
    needle.toLowerCase() === 'others'
  ) {
    return false;
  }

  try {
    const items = await fetchOrganizationsByPrefix(needle, { exact: true });
    return items.some(
      (item) =>
        String(item?.organizationName ?? '').trim().toLowerCase() ===
        needle.toLowerCase(),
    );
  } catch (error) {
    logError('[checkOrganisationListed] Lookup failed', error);
    return false;
  }
};